import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  QueryList,
  SimpleChanges,
  ViewChildren
} from '@angular/core';
import { Subscribable } from '../../../../models/base/subscribable';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { InvestmentInstruction } from '../../../../models/transactions/investment-instruction';

@Component({
  selector: 'app-investment-instructions-visualization',
  templateUrl: './investment-instructions-visualization.component.html',
  styleUrls: ['./investment-instructions-visualization.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InvestmentInstructionsVisualizationComponent
  extends Subscribable
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input() instructions: InvestmentInstruction[] = [];
  @Input() label: string = '';
  @Input() cardBackgroundColour: string = '#FFFFFF';
  @Input() cardBorderColour: string = '#CACACA';

  // When multiple related visualization components are displayed together, the parent component should set
  // `forcedColumns` to the length of the longest `investmentInstructions` array among them. This ensures that
  // each component uses a consistent column count, maintaining visual alignment and sizing across all views.
  @Input() forcedColumns: number = 0;

  @ViewChildren('grid') gridElements!: QueryList<ElementRef>;

  readonly COLUMN_MIN_WIDTH = 125;
  readonly GAP_PX = 16;
  readonly MAX_ALLOWED_COLUMNS = 4;

  protected observer!: ResizeObserver;

  protected _forcedColumns = new BehaviorSubject(1);
  public forcedColumns$ = this._forcedColumns as Observable<number>;

  protected _gridWidth = new BehaviorSubject(0);
  public gridWidth$ = this._gridWidth as Observable<number>;

  public gridStyles$ = combineLatest([this.forcedColumns$, this.gridWidth$]).pipe(
    map(([forcedCols, gridWidth]) => {
      const gap = this.GAP_PX;
      const maxColumnsFit = Math.floor(gridWidth / (this.COLUMN_MIN_WIDTH + gap));
      const nColumnsToUse = Math.min(maxColumnsFit, forcedCols, this.MAX_ALLOWED_COLUMNS);

      const gridTemplateColumns = `repeat(${nColumnsToUse}, minmax(${this.COLUMN_MIN_WIDTH}px, 1fr))`;
      return {
        display: 'grid',
        'grid-template-columns': gridTemplateColumns,
        gap: `${gap}px`
      };
    })
  );

  public ngAfterViewInit(): void {
    this.initializeObserver();

    this.gridElements.changes.subscribeWhileAlive({
      owner: this,
      next: () => {
        this.initializeObserver();
      }
    });
  }

  private initializeObserver(): void {
    this.observer?.disconnect();
    this.observer = new ResizeObserver(() => this.updateGridWidth());
    if (this.gridElements.length > 0) {
      this.observer.observe(this.gridElements.first.nativeElement);
    }
  }

  protected updateGridWidth(): void {
    if (this.gridElements.length > 0) {
      const gridWidth: number = this.gridElements.first.nativeElement.clientWidth;
      this._gridWidth.next(gridWidth);
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.forcedColumns) {
      this._forcedColumns.next(this.forcedColumns);
    }
    // If no forced columns are set, calculate using the length of the instructions array
    if (changes.instructions && !this.forcedColumns) {
      this._forcedColumns.next(this.instructions?.length ?? 0);
    }
  }

  public ngOnDestroy(): void {
    this.observer?.disconnect();
  }
}
